/**
 * useModuleEditing — the editing bundle the details sidebar consumes
 * (W1, WYSIWYG plan §7). Bridges the selected module to the mutation
 * service + LSP rename channel and answers the provenance questions the
 * inline controls need (authored vs inherited, single-vs-multi description,
 * unnamed). Sidebar stays a pure view: it receives this bundle as ONE
 * optional prop and renders inputs when present.
 */
import { useMemo } from "react";
import {
  createSpanLookup,
  fieldEditorText,
  renderValueLiteral,
  type ModuleLocation,
  type SpanLookup,
} from "@archlang/engine";
import { useWorkspace } from "./WorkspaceContext.js";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import {
  useModelMutations,
  type ModelMutationsApi,
  type MutationCompileState,
  type MutationResult,
} from "./mutations.js";
import { useLspClient, type LspRenameClient } from "./lspClient.js";
import type { WorkspaceStore } from "./WorkspaceStore.js";

export interface ModuleEditing {
  /** Wire/board id of the module the bundle addresses. */
  readonly targetId: string;
  /** Authored description: editable iff exactly one authored string entry
   *  (or none yet). `null` target = stub/foreign — description not editable. */
  readonly description: { readonly text: string; readonly editable: boolean };
  /** Field paths authored on the instance itself (everything else shown is
   *  inherited/stamped → edit inserts a local override, remove emits `drop`). */
  readonly authoredFieldPaths: ReadonlySet<string>;
  /** Authored SOURCE text per authored field path — a getter/cast reads back as
   *  the expression (`@@team`), not the value it resolved to. */
  readonly authoredFieldText: ReadonlyMap<string, string>;
  readonly authoredAspectPaths: ReadonlySet<string>;
  /** Type-registry kind names for the kind field's datalist. */
  readonly typeOptions: readonly string[];
  /** True when the whole target is editable (authored, in the user VFS). */
  readonly editable: boolean;
  /** Rename affordance state: unnamed modules always (setName); named ones
   *  only while an LSP client is live (cross-file reference rewrite). */
  readonly renameEnabled: boolean;
  readonly unnamed: boolean;

  setDescription(text: string): MutationResult;
  setField(path: string, rawValue: string): MutationResult;
  removeField(path: string): MutationResult;
  setAspect(path: string, rawValue: string): MutationResult;
  removeAspect(path: string): MutationResult;
  setType(typeName: string): MutationResult;
  rename(newName: string): Promise<MutationResult>;
}

// Moved to the engine (W4 — shared by CLI/MCP headless writes); re-exported
// so existing imports keep working.
export { renderValueLiteral };

const REFUSED_NO_TARGET: MutationResult = { status: "unknown-target" };

export function useModuleEditing(targetId: string | null): ModuleEditing | null {
  const store = useWorkspace();
  const shared = useWorkspaceModel();
  const mutations = useModelMutations();
  const lsp = useLspClient(store);

  const lookup: SpanLookup | null = useMemo(
    () => (shared.resolvedModel ? createSpanLookup(shared.resolvedModel) : null),
    [shared.resolvedModel],
  );

  return useMemo(
    () => buildModuleEditing({ store, shared, mutations, lsp, lookup, targetId }),
    [targetId, lookup, shared, mutations, lsp, store],
  );
}

/** The pure core behind {@link useModuleEditing} — testable with a real store
 *  + headless compile, no React tree. */
export function buildModuleEditing(deps: {
  store: Pick<WorkspaceStore, "getState">;
  shared: {
    resolvedModel: MutationCompileState["resolvedModel"];
    compiledSources: MutationCompileState["compiledSources"];
  };
  mutations: ModelMutationsApi;
  lsp: LspRenameClient | null;
  lookup: SpanLookup | null;
  targetId: string | null;
}): ModuleEditing | null {
  const { store, shared, mutations, lsp, lookup, targetId } = deps;
  {
    if (!targetId || !lookup) return null;
    const loc = lookup.moduleFor(targetId);
    if (!loc) return null;

    // §10.4 writability: only files in the user VFS are editable (stdlib and
    // template files never are; storeKeyFor inside the service double-guards).
    const absFile = loc.file;
    const inUserVfs =
      absFile !== undefined && (shared.compiledSources?.has(absFile) ?? false);
    const editable = inUserVfs && loc.materialization !== "stub";

    const descriptions = loc.decl.body.descriptions;
    const description = {
      text: descriptions.map((d) => d.text).join("\n"),
      editable: editable && descriptions.length <= 1,
    };

    const authoredFieldPaths = new Set(loc.decl.body.fields.map((f) => f.path.join(".")));
    const authoredAspectPaths = new Set(loc.decl.body.aspects.map((a) => a.path.join(".")));

    // What the AUTHOR wrote, per authored field — which for a getter or cast
    // (`widget.color: colorize(@@team)`) is not what the model resolved it to.
    // The editor must show and round-trip the rule; showing the resolved
    // constant would let a plain re-save freeze it (spec §4.3.2).
    const authoredFieldText = new Map<string, string>();
    for (const f of loc.decl.body.fields) {
      if (f.value === undefined) continue;
      authoredFieldText.set(f.path.join("."), fieldEditorText(f.value));
    }

    const typeOptions = (shared.resolvedModel?.types ?? [])
      .map((t) => t.name)
      .filter((n): n is string => typeof n === "string" && n.length > 0)
      .sort();

    const guard = (fn: () => MutationResult): MutationResult =>
      editable ? fn() : REFUSED_NO_TARGET;

    const renameViaLsp = async (l: ModuleLocation, newName: string): Promise<MutationResult> => {
      if (!lsp || !l.file || !l.nameSpan) return REFUSED_NO_TARGET;
      // Capture EVERY file's text now — offsets are computed against this
      // snapshot and the apply guard verifies it (plan §6).
      const texts = new Map<string, string>();
      for (const [k, v] of store.getState().files) texts.set(k.startsWith("/") ? k : "/" + k, v);
      const fileEdits = await lsp.rename(
        l.file,
        l.nameSpan.start.line,
        l.nameSpan.start.column,
        newName,
        texts,
      );
      if (!fileEdits || fileEdits.length === 0) {
        return { status: "refused", reason: "rename", message: "rename rejected by the language service" };
      }
      return mutations.applyExternalEdits(
        fileEdits.map((f) => ({ path: f.path, expectedText: texts.get(f.path)!, edits: f.edits })),
      );
    };

    return {
      targetId,
      description,
      authoredFieldPaths,
      authoredFieldText,
      authoredAspectPaths,
      typeOptions,
      editable,
      unnamed: loc.decl.unnamed === true,
      renameEnabled: editable && (loc.decl.unnamed === true || lsp !== null),

      setDescription: (text) =>
        guard(() => mutations.mutateModule(targetId, { op: "setDescription", text })),
      setField: (path, rawValue) =>
        guard(() =>
          mutations.mutateModule(targetId, {
            op: "setField",
            path: path.split("."),
            value: renderValueLiteral(rawValue),
          }),
        ),
      removeField: (path) =>
        guard(() => mutations.mutateModule(targetId, { op: "removeField", path: path.split(".") })),
      setAspect: (path, rawValue) =>
        guard(() =>
          mutations.mutateModule(targetId, {
            op: "setAspect",
            path: path.split("."),
            value: renderValueLiteral(rawValue),
          }),
        ),
      removeAspect: (path) =>
        guard(() => mutations.mutateModule(targetId, { op: "removeAspect", path: path.split(".") })),
      setType: (typeName) =>
        guard(() => mutations.mutateModule(targetId, { op: "setType", typeName })),

      rename: async (newName) => {
        if (!editable) return REFUSED_NO_TARGET;
        if (loc.decl.unnamed) {
          return mutations.mutateModule(targetId, { op: "setName", name: newName });
        }
        return renameViaLsp(loc, newName);
      },
    };
  }
}
